// The invoice editor: prefilled from the child's enrolment, funding and previous invoice, every number editable,
// with a live total. Issues a new invoice or reissues an existing one.

import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { PdfDialog } from "./PdfDialog";
import {
  RATES,
  addDays,
  formatPounds,
  invoiceAmounts,
  prefillTermCalc,
  registrationExtras,
  termHours,
  today,
  validateInput,
  type ExtraLine,
  type PreschoolChild,
  type PreschoolInvoice,
  type PreschoolInvoiceInput,
  type PreschoolInvoiceKind,
  type PreschoolTerm,
  type TermCalc,
} from "../../shared/preschoolInvoices";

/** `open`: the term's open days from the calendar (exact hours), or null to use the term's usual weeks. */
export type EditorTarget =
  | { mode: "create"; child: PreschoolChild | null; previous: PreschoolInvoice | null; term: PreschoolTerm; open: string[] | null }
  | { mode: "edit"; invoice: PreschoolInvoice; term: PreschoolTerm; open: string[] | null };

/** A number box that keeps what is typed and reports a number (NaN while invalid). */
const NumberField: React.FC<{
  label: string; value: number; onChange: (value: number) => void; unit?: "£" | "%"; pence?: boolean; width?: number | string;
}> = ({ label, value, onChange, unit, pence, width }) => {
  const shown = (v: number) => (isNaN(v) ? "" : pence ? formatPounds(v) : String(v));
  const [text, setText] = useState(shown(value));
  useEffect(() => {
    const parsed = parseFloat(text);
    const current = pence ? Math.round(parsed * 100) : parsed;
    if (current !== value) setText(shown(value));
  }, [value]);
  const invalid = text.trim() === "" || isNaN(Number(text));
  return (
    <TextField
      label={label}
      size="small"
      value={text}
      error={invalid}
      inputProps={{ inputMode: "decimal" }}
      sx={{ width: width ?? "100%" }}
      onChange={(e) => {
        setText(e.target.value);
        const parsed = Number(e.target.value);
        onChange(e.target.value.trim() === "" || isNaN(parsed) ? NaN : pence ? Math.round(parsed * 100) : parsed);
      }}
      InputProps={unit === "£"
        ? { startAdornment: <InputAdornment position="start">£</InputAdornment> }
        : unit === "%" ? { endAdornment: <InputAdornment position="end">%</InputAdornment> } : undefined}
    />
  );
};

const Section: React.FC<{ title: string; total: string; hint?: string; children: React.ReactNode }> = ({ title, total, hint, children }) => (
  <Box>
    <Stack direction="row" alignItems="baseline" sx={{ mb: 1 }}>
      <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>{title}{hint && <Typography component="span" variant="caption" color="text.secondary"> · {hint}</Typography>}</Typography>
      <Typography variant="subtitle2" sx={{ fontVariantNumeric: "tabular-nums" }}>{total}</Typography>
    </Stack>
    <Grid container spacing={1.5}>{children}</Grid>
  </Box>
);

const cell = (node: React.ReactNode, xs = 6, sm = 2) => <Grid item xs={xs} sm={sm}>{node}</Grid>;

const defaultDue = (term: PreschoolTerm) => {
  const now = today();
  const beforeStart = term.startDate ? addDays(term.startDate, -7) : null;
  return beforeStart && beforeStart > now ? beforeStart : addDays(now, 7);
};

const QUICK_EXTRAS: ExtraLine[] = [
  { description: "Early Bird Start", quantity: 1, unitPricePence: 450 },
  { description: "Deposit returned", quantity: 1, unitPricePence: -RATES.depositPence },
  { description: "Late collection", quantity: 1, unitPricePence: 500 },
];

export const InvoiceEditor: React.FC<{
  target: EditorTarget | null;
  children: PreschoolChild[];
  invoices: PreschoolInvoice[];
  onClose: () => void;
  onSubmit: (input: PreschoolInvoiceInput) => Promise<void>;
}> = ({ target, children, invoices, onClose, onSubmit }) => {
  const fullScreen = useMediaQuery("(max-width: 700px)");
  const [input, setInput] = useState<PreschoolInvoiceInput | null>(null);
  const [notes, setNotes] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);

  const prefill = (child: PreschoolChild | null, kind: PreschoolInvoiceKind, term: PreschoolTerm, previous: PreschoolInvoice | null): PreschoolInvoiceInput => {
    const base = { childId: child?.id ?? "", termId: term.id, dueDate: defaultDue(term) };
    if (kind === "registration") return { ...base, kind, period: "Registration", calc: null, extras: registrationExtras() };
    if (kind === "other") return { ...base, kind, period: term.name, calc: null, extras: [{ description: "", quantity: 1, unitPricePence: 0 }] };
    if (!child) return { ...base, kind, period: term.name, calc: null, extras: [] };
    const prefilled = prefillTermCalc(child, term, previous, today(), target?.open ?? null);
    setNotes(prefilled.notes);
    return { ...base, kind, period: term.name, calc: prefilled.calc, extras: [] };
  };

  useEffect(() => {
    setError(null);
    setSaving(false);
    setNotes([]);
    if (!target) return setInput(null);
    if (target.mode === "edit") {
      const { invoice } = target;
      setInput({ kind: invoice.kind, childId: invoice.childId, termId: invoice.termId, period: invoice.period,
        dueDate: invoice.dueDate || defaultDue(target.term), calc: invoice.calc, extras: invoice.extras });
    } else {
      setInput(prefill(target.child, "term", target.term, target.previous));
    }
  }, [target]);

  const amounts = useMemo(() => (input ? invoiceAmounts(input.calc, input.extras) : null), [input]);
  if (!target || !input || !amounts) return null;

  const child = children.find((c) => c.id === input.childId) ?? null;
  const problems = validateInput(input);
  const editing = target.mode === "edit";
  const setCalc = (update: (calc: TermCalc) => TermCalc) => setInput({ ...input, calc: input.calc ? update(input.calc) : input.calc });
  const setExtra = (index: number, change: Partial<ExtraLine>) =>
    setInput({ ...input, extras: input.extras.map((line, i) => (i === index ? { ...line, ...change } : line)) });
  const lineTotal = (description: string) => amounts.items.find((i) => i.description.startsWith(description))?.totalPence ?? 0;

  const changeChild = (next: PreschoolChild | null) => {
    if (input.kind === "term") {
      const previous = next ? invoices.filter((i) => i.childId === next.id && i.kind === "term" && i.calc && !i.cancelled)
        .sort((a, b) => b.invoiceNo - a.invoiceNo)[0] ?? null : null;
      setInput({ ...prefill(next, "term", target.term, previous), dueDate: input.dueDate, period: input.period });
    } else {
      setInput({ ...input, childId: next?.id ?? "" });
    }
  };

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSubmit(input);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  };

  const calc = input.calc;
  const existing = target.mode === "edit" ? target.invoice : null;
  const issuing = !existing || !!existing.notIssued;
  /** What the PDF would look like with the values as they are now. */
  const draft: PreschoolInvoice = {
    ...(existing ?? {
      ref: "BHP····", invoiceNo: 0, paid: false, paidDate: null, cancelled: false, draftId: null, draftedAt: null,
      draftError: null, pdfPath: null, receipt: null,
    }),
    ...input,
    ...amounts,
    childName: child?.name ?? existing?.childName ?? "",
    parentName: child?.parentName ?? existing?.parentName ?? "",
    address: child?.address ?? existing?.address ?? "",
    email: child?.email ?? existing?.email ?? "",
    issuedDate: today(),
    pdfPath: null,
    drivePdfId: null,
    receipt: null,
  };
  return (
    <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="md" fullScreen={fullScreen}>
      <DialogTitle>{existing ? `${issuing ? "Issue" : "Edit and reissue"} ${existing.ref}` : "New invoice"}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <Autocomplete
              sx={{ flex: 2 }}
              size="small"
              disabled={editing}
              options={children}
              value={child}
              getOptionLabel={(c) => `${c.name} (${c.parentName})`}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              onChange={(_, value) => changeChild(value)}
              renderInput={(params) => <TextField {...params} label="Child" />}
            />
            <ToggleButtonGroup size="small" exclusive value={input.kind} disabled={editing}
              onChange={(_, kind: PreschoolInvoiceKind | null) => kind && setInput({ ...prefill(child, kind, target.term,
                child ? invoices.filter((i) => i.childId === child.id && i.kind === "term" && i.calc && !i.cancelled).sort((a, b) => b.invoiceNo - a.invoiceNo)[0] ?? null : null),
              dueDate: input.dueDate })}>
              <ToggleButton value="term">Term</ToggleButton>
              <ToggleButton value="registration">Registration</ToggleButton>
              <ToggleButton value="other">Other</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField size="small" label="Invoice period" value={input.period} sx={{ flex: 2 }}
              onChange={(e) => setInput({ ...input, period: e.target.value })} />
            <TextField size="small" label="Due date" type="date" value={input.dueDate} sx={{ flex: 1 }} InputLabelProps={{ shrink: true }}
              onChange={(e) => setInput({ ...input, dueDate: e.target.value })} />
          </Stack>

          {notes.length > 0 && (
            <Alert severity="info" icon={false} sx={{ py: 0 }}>
              <Typography variant="body2">Prefilled: {notes.join(" · ")}. Check and adjust anything.</Typography>
            </Alert>
          )}

          {calc && (
            <>
              <Section title="Funded hours"
                hint={`${termHours(calc.funded)} h free to the parent; council pays £${formatPounds(amounts.fundingPence)}`}
                total={`£${formatPounds(lineTotal("Consumable"))}`}>
                {cell(<NumberField label="Weeks" value={calc.funded.weeks} onChange={(v) => setCalc((c) => ({ ...c, funded: { ...c.funded, weeks: v } }))} />)}
                {cell(<NumberField label="Hours a week" value={calc.funded.hoursPerWeek} onChange={(v) => setCalc((c) => ({ ...c, funded: { ...c.funded, hoursPerWeek: v } }))} />)}
                {cell(<NumberField label="Extra hours" value={calc.funded.extraHours} onChange={(v) => setCalc((c) => ({ ...c, funded: { ...c.funded, extraHours: v } }))} />)}
                {cell(<NumberField label="Consumables" unit="£" pence value={calc.funded.consumableRatePence} onChange={(v) => setCalc((c) => ({ ...c, funded: { ...c.funded, consumableRatePence: v } }))} />)}
                {cell(<NumberField label="Discount" unit="%" value={calc.funded.discountPercent} onChange={(v) => setCalc((c) => ({ ...c, funded: { ...c.funded, discountPercent: v } }))} />)}
                {cell(<NumberField label="Council rate" unit="£" pence value={calc.funded.fundingRatePence ?? 0} onChange={(v) => setCalc((c) => ({ ...c, funded: { ...c.funded, fundingRatePence: v } }))} />)}
              </Section>
              <Section title="Private hours" hint={`${termHours(calc.private)} h`} total={`£${formatPounds(lineTotal("Private"))}`}>
                {cell(<NumberField label="Weeks" value={calc.private.weeks} onChange={(v) => setCalc((c) => ({ ...c, private: { ...c.private, weeks: v } }))} />)}
                {cell(<NumberField label="Hours a week" value={calc.private.hoursPerWeek} onChange={(v) => setCalc((c) => ({ ...c, private: { ...c.private, hoursPerWeek: v } }))} />)}
                {cell(<NumberField label="Extra hours" value={calc.private.extraHours} onChange={(v) => setCalc((c) => ({ ...c, private: { ...c.private, extraHours: v } }))} />)}
                {cell(<NumberField label="Hourly rate" unit="£" pence value={calc.private.ratePence} onChange={(v) => setCalc((c) => ({ ...c, private: { ...c.private, ratePence: v } }))} />)}
                {cell(<NumberField label="Discount" unit="%" value={calc.private.discountPercent} onChange={(v) => setCalc((c) => ({ ...c, private: { ...c.private, discountPercent: v } }))} />)}
              </Section>
              <Section title="Lunch" hint="morning-only days" total={`£${formatPounds(lineTotal("Lunch"))}`}>
                {cell(<NumberField label="Weeks" value={calc.lunch.weeks} onChange={(v) => setCalc((c) => ({ ...c, lunch: { ...c.lunch, weeks: v } }))} />, 6, 3)}
                {cell(<NumberField label="Days a week" value={calc.lunch.daysPerWeek} onChange={(v) => setCalc((c) => ({ ...c, lunch: { ...c.lunch, daysPerWeek: v } }))} />, 6, 3)}
                {cell(<NumberField label="Extra days" value={calc.lunch.extraDays} onChange={(v) => setCalc((c) => ({ ...c, lunch: { ...c.lunch, extraDays: v } }))} />, 6, 3)}
                {cell(<NumberField label="Per day" unit="£" pence value={calc.lunch.ratePence} onChange={(v) => setCalc((c) => ({ ...c, lunch: { ...c.lunch, ratePence: v } }))} />, 6, 3)}
              </Section>
            </>
          )}

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>{calc ? "Extra lines" : "Lines"}</Typography>
            <Stack spacing={1}>
              {input.extras.map((line, index) => (
                <Stack key={index} direction="row" spacing={1} alignItems="center">
                  <TextField size="small" label="Description" value={line.description} sx={{ flex: 1 }}
                    error={!line.description.trim()} onChange={(e) => setExtra(index, { description: e.target.value })} />
                  <NumberField label="Qty" width={80} value={line.quantity} onChange={(v) => setExtra(index, { quantity: v })} />
                  <NumberField label="Unit price" width={120} unit="£" pence value={line.unitPricePence} onChange={(v) => setExtra(index, { unitPricePence: v })} />
                  <IconButton aria-label="Remove line" onClick={() => setInput({ ...input, extras: input.extras.filter((_, i) => i !== index) })}>
                    <DeleteOutlineIcon />
                  </IconButton>
                </Stack>
              ))}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip icon={<AddIcon />} label="Line" variant="outlined" clickable
                  onClick={() => setInput({ ...input, extras: [...input.extras, { description: "", quantity: 1, unitPricePence: 0 }] })} />
                {QUICK_EXTRAS.map((extra) => (
                  <Chip key={extra.description} icon={<AddIcon />} variant="outlined" clickable
                    label={`${extra.description} (${extra.unitPricePence < 0 ? "−" : ""}£${formatPounds(Math.abs(extra.unitPricePence))})`}
                    onClick={() => setInput({ ...input, extras: [...input.extras, { ...extra }] })} />
                ))}
              </Stack>
            </Stack>
          </Box>

          <Divider />
          <Stack direction="row" justifyContent="flex-end" spacing={4}>
            <Box textAlign="right">
              <Typography variant="body2" color="text.secondary">Subtotal £{formatPounds(amounts.subtotalPence)}</Typography>
              <Typography variant="body2" color="text.secondary">Discount −£{formatPounds(amounts.discountPence)}</Typography>
              <Typography variant="h5" fontWeight={600} sx={{ fontVariantNumeric: "tabular-nums" }}>£{formatPounds(amounts.totalPence)}</Typography>
              <Typography variant="caption" color="text.secondary">parent pays · plus £{formatPounds(amounts.fundingPence)} council funding</Typography>
            </Box>
          </Stack>
          {error && <Alert severity="error">{error}</Alert>}
          {problems.length > 0 && !error && <Alert severity="warning">{problems.join(" ")}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1, pl: 2 }}>
          {child ? `Gmail draft to ${child.email || "no email!"} · nothing is sent` : ""}
        </Typography>
        <Button startIcon={<VisibilityIcon />} onClick={() => setPreviewing(true)}>Preview</Button>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" disabled={problems.length > 0 || saving} onClick={submit}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}>
          {issuing ? "Issue invoice" : "Reissue invoice"}
        </Button>
      </DialogActions>
      {previewing && <PdfDialog invoice={draft} title={`Preview · ${draft.ref}`} onClose={() => setPreviewing(false)} />}
    </Dialog>
  );
};
